import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';

function ClientView({ apiBase, loading, setLoading, error, setError }) {
  const [data, setData] = useState(null);
  const [markup, setMarkup] = useState(20);

  useEffect(() => {
    loadData();
  }, [markup]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiBase}/analytics/by-client?markup_percentage=${markup}`);
      setData(response.data);
    } catch (err) {
      setError('Failed to load client data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading data...</div>;
  }

  if (!data || data.length === 0) {
    return <div className="loading">No client data available</div>;
  }

  const totalCalls = data.reduce((sum, d) => sum + d.total_calls, 0);
  const totalCost = data.reduce((sum, d) => sum + d.total_cost, 0);
  const totalPrice = data.reduce((sum, d) => sum + d.total_price, 0);
  const totalProfit = data.reduce((sum, d) => sum + d.profit_margin, 0);

  const labels = data.map(d => d.client_name || d.client_id);

  const revenueChartData = {
    labels: labels,
    datasets: [
      {
        label: 'Cost',
        data: data.map(d => d.total_cost / 100),
        backgroundColor: '#f56565'
      },
      {
        label: 'Price to Client',
        data: data.map(d => d.total_price / 100),
        backgroundColor: '#667eea'
      }
    ]
  };

  const profitChartData = {
    labels: labels,
    datasets: [{
      label: 'Profit ($)',
      data: data.map(d => d.profit_margin / 100),
      backgroundColor: '#48bb78'
    }]
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'bottom'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '$' + value.toFixed(2);
          }
        }
      }
    }
  };

  const profitOptions = {
    ...barOptions,
    plugins: {
      legend: {
        display: false
      }
    }
  };

  return (
    <>
      <div className="controls" style={{ marginTop: '20px' }}>
        <div className="control-group">
          <label htmlFor="markup">Markup %</label>
          <input 
            type="number" 
            id="markup" 
            value={markup} 
            onChange={(e) => setMarkup(e.target.value)}
            min="0" 
            max="100" 
            step="5"
          />
        </div>
        <button onClick={loadData}>🔄 Refresh</button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Clients</h3>
          <div className="value">{data.length}</div>
          <div className="subvalue">active clients</div>
        </div>
        <div className="stat-card">
          <h3>Total Calls</h3>
          <div className="value">{totalCalls.toLocaleString()}</div>
          <div className="subvalue">across all clients</div>
        </div>
        <div className="stat-card">
          <h3>Your Cost</h3>
          <div className="value cost">${(totalCost / 100).toFixed(2)}</div>
          <div className="subvalue">paid to ElevenLabs</div>
        </div>
        <div className="stat-card">
          <h3>Client Billing</h3>
          <div className="value price">${(totalPrice / 100).toFixed(2)}</div>
          <div className="subvalue">to charge clients</div>
        </div>
        <div className="stat-card">
          <h3>Total Profit</h3>
          <div className="value profit">${(totalProfit / 100).toFixed(2)}</div>
          <div className="subvalue">{markup}% markup</div>
        </div>
        <div className="stat-card">
          <h3>Profit Margin</h3>
          <div className="value profit">{((totalProfit / totalPrice) * 100).toFixed(1)}%</div>
          <div className="subvalue">effective margin</div>
        </div>
      </div>

      <div className="chart-grid">
        <div className="chart-container">
          <h2>Revenue by Client</h2>
          <Bar data={revenueChartData} options={barOptions} />
        </div>
        <div className="chart-container">
          <h2>Profit by Client</h2>
          <Bar data={profitChartData} options={profitOptions} />
        </div>
      </div>

      <div className="table-container">
        <h2>Client Billing Details ({markup}% Markup)</h2>
        <table>
          <thead>
            <tr>
              <th>Client</th>
              <th>Calls</th>
              <th>Credits</th>
              <th>Your Cost</th>
              <th>Price to Client</th>
              <th>Profit</th>
              <th>Margin %</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d, index) => (
              <tr key={index}>
                <td><strong>{d.client_name || d.client_id}</strong></td>
                <td>{d.total_calls}</td>
                <td>{d.total_credits_used.toLocaleString()}</td>
                <td className="cost">${(d.total_cost / 100).toFixed(2)}</td>
                <td className="price">${(d.total_price / 100).toFixed(2)}</td>
                <td className="profit">${(d.profit_margin / 100).toFixed(2)}</td>
                <td>{((d.profit_margin / d.total_price) * 100).toFixed(1)}%</td>
              </tr>
            ))}
            <tr style={{ fontWeight: 'bold', background: '#f7fafc' }}>
              <td>TOTAL</td>
              <td>{totalCalls}</td>
              <td>{data.reduce((sum, d) => sum + d.total_credits_used, 0).toLocaleString()}</td>
              <td className="cost">${(totalCost / 100).toFixed(2)}</td>
              <td className="price">${(totalPrice / 100).toFixed(2)}</td>
              <td className="profit">${(totalProfit / 100).toFixed(2)}</td>
              <td>{((totalProfit / totalPrice) * 100).toFixed(1)}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

export default ClientView;

